import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import beReliableLogo from "../../assets/logo/bereliable-logo.png";
import './AdminLogin.css';

const AdminLogin = () => {
  // ================= LOGIN =================
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });

  // ================= REGISTER =================
  const [showRegister, setShowRegister] = useState(false);
  const [step, setStep] = useState(1); // 1=register, 2=otp
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    otp: ''
  });

  // ================= FORGOT PASSWORD =================
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=email, 2=otp+password
  const [resetData, setResetData] = useState({
    email: '',
    otp: '',
    newPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  const { login } = useAuth();
  const navigate = useNavigate();

  // ================= AUTH CHECK =================
  useEffect(() => {
    const adminAuth = localStorage.getItem('adminAuth');
    if (adminAuth === 'true') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  // ================= HANDLERS =================
  const handleLoginChange = (e) =>
    setLoginData({ ...loginData, [e.target.name]: e.target.value });

  const handleRegisterChange = (e) =>
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });

  const handleResetChange = (e) =>
    setResetData({ ...resetData, [e.target.name]: e.target.value });

  // ================= LOGIN =================
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginData.username, loginData.password);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      } else {
        toast.error(result.message || 'Login failed');
      }
    } catch (err) {
      toast.error('Login failed');
    }
    setLoading(false);
  };

  // ================= REGISTER =================
  const submitRegister = async () => {
    try {
      const res = await axios.post('/auth/register', {
        username: registerData.username,
        email: registerData.email,
        password: registerData.password
      });
      toast.success(res.data.message);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    }
  };

  const submitOtp = async () => {
    try {
      const res = await axios.post('/auth/verify-otp', {
        email: registerData.email,
        otp: registerData.otp
      });
      toast.success(res.data.message);
      setShowRegister(false);
      setStep(1);
      setRegisterData({ username: '', email: '', password: '', otp: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'OTP verification failed');
    }
  };

  // ================= FORGOT PASSWORD =================
  const sendResetOtp = async () => {
    if (otpCooldown > 0) return;

    try {
      const res = await axios.post('/auth/forgot-password', {
        email: resetData.email
      });
      toast.success(res.data.message || 'OTP sent to email');
      setForgotStep(2);
      setOtpCooldown(60);

      // Start cooldown timer
      const interval = setInterval(() => {
        setOtpCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    }
  };

  const submitResetPassword = async () => {
    try {
      const res = await axios.post('/auth/reset-password', resetData);
      toast.success(res.data.message);
      setShowForgot(false);
      setForgotStep(1);
      setResetData({ email: '', otp: '', newPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password reset failed');
    }
  };

  // ================= UI =================
  return (
    <div className="admin-login-page">
      <div className="login-container">
        <div className="login-card card">
          <div className="login-header">
            <img src={beReliableLogo} className="admin-login-logo" alt="Logo" />
            <h2>
              {showRegister
                ? step === 1 ? 'Admin Registration' : 'Verify OTP'
                : showForgot
                ? forgotStep === 1 ? 'Forgot Password' : 'Reset Password'
                : 'Admin Login'}
            </h2>
            <p>
              {showRegister
                ? step === 1
                  ? 'Create your admin account'
                  : 'Enter the OTP sent to your email'
                : showForgot
                ? forgotStep === 1
                  ? 'Enter your email to receive OTP'
                  : 'Enter OTP and your new password'
                : 'Enter your credentials to access the admin dashboard'}
            </p>
          </div>

          {/* ================= LOGIN ================= */}
          {!showRegister && !showForgot && (
            <form onSubmit={handleLoginSubmit} className="login-form">
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  name="username"
                  className="form-input"
                  value={loginData.username}
                  onChange={handleLoginChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  name="password"
                  className="form-input"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                />
              </div>

              <div className="login-links-row">
                <span
                  className="login-link"
                  onClick={() => setShowForgot(true)}
                >
                  Forgot Password?
                </span>
                <span
                  className="login-link"
                  onClick={() => setShowRegister(true)}
                >
                  New Register
                </span>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}

          {/* ================= REGISTER ================= */}
          {showRegister && step === 1 && (
            <div className="login-form">
              <div className="form-group">
                <label className="form-label">Username</label>
                <input name="username" className="form-input" onChange={handleRegisterChange} />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input name="email" className="form-input" onChange={handleRegisterChange} />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input type="password" name="password" className="form-input" onChange={handleRegisterChange} />
              </div>

              <button className="btn btn-primary btn-block" onClick={submitRegister}>
                Register
              </button>

              <div className="login-links-row">
                <span className="login-link" onClick={() => setShowRegister(false)}>
                  Back to Login
                </span>
              </div>
            </div>
          )}

          {/* ================= REGISTER OTP ================= */}
          {showRegister && step === 2 && (
            <div className="login-form">
              <div className="form-group">
                <label className="form-label">OTP</label>
                <input name="otp" className="form-input" onChange={handleRegisterChange} />
              </div>

              <button className="btn btn-primary btn-block" onClick={submitOtp}>
                Verify OTP
              </button>
            </div>
          )}

          {/* ================= FORGOT PASSWORD ================= */}
          {showForgot && forgotStep === 1 && (
            <div className="login-form">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  onChange={handleResetChange}
                  required
                />
              </div>

              <button
                className="btn btn-primary btn-block"
                onClick={sendResetOtp}
                disabled={otpCooldown > 0}
              >
                {otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : 'Send OTP'}
              </button>

              <div className="login-links-row">
                <span className="login-link" onClick={() => setShowForgot(false)}>
                  Back to Login
                </span>
              </div>
            </div>
          )}

          {showForgot && forgotStep === 2 && (
            <div className="login-form">
              <div className="form-group">
                <label className="form-label">OTP</label>
                <input name="otp" className="form-input" onChange={handleResetChange} required />
              </div>

              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  className="form-input"
                  onChange={handleResetChange}
                  required
                />
              </div>

              <button className="btn btn-primary btn-block" onClick={submitResetPassword}>
                Reset Password
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
